import { Injectable, inject, computed } from '@angular/core';
import { UserStore } from '../app-state/user.store';

@Injectable({ providedIn: 'root' })
export class RoleService {
  private readonly userStore = inject(UserStore);

  // Direct computed signals from store
  private readonly role = computed(() => this.userStore.role());

  /**
   * Normalizes roles (handles alias mapping but keeps organization-owner explicit)
   */
  private normalizeRole(role: string | null): string | null {
    if (!role) return null;

    const map: Record<string, string> = {
      administrator: 'app-admin',
      owner: 'organization-owner',
    };

    // Keep organization-owner as its own canonical role
    return map[role] || role;
  }

  /**
   * Checks if the current role matches the required one, treating
   * owner and organization-owner as equivalent for access.
   */
  hasRole(requiredRole: string): boolean {
    const currentRole = this.normalizeRole(this.role());
    const normalizedRequired = this.normalizeRole(requiredRole);
    if (!currentRole || !normalizedRequired) return false;

    return this.rolesMatch(currentRole, normalizedRequired);
  }

  /**
   * Check if user has any role from a list.
   */
  hasAnyRole(requiredRoles: string[]): boolean {
    const currentRole = this.normalizeRole(this.role());
    if (!currentRole) return false;

    return requiredRoles.some((r) => {
      const normalizedRequired = this.normalizeRole(r);
      return normalizedRequired
        ? this.rolesMatch(currentRole, normalizedRequired)
        : false;
    });
  }

  /**
   * Hierarchical role check — determines if user has at least the required access level.
   * Example: app-readonly < app-user < app-admin < owner / organization-owner
   */
  hasAtLeastRole(requiredRole: string): boolean {
    const hierarchy = [
      'app-readonly',
      'app-user',
      'app-admin',
      'owner',
      'organization-owner',
    ];
    const currentRole = this.normalizeRole(this.role());
    const normalizedRequired = this.normalizeRole(requiredRole);

    if (!currentRole || !normalizedRequired) return false;

    const requiredLevel = hierarchy.indexOf(normalizedRequired);
    const currentLevel = hierarchy.indexOf(currentRole);

    // An unrecognized role must fail closed rather than satisfy every
    // requirement — indexOf's -1 would otherwise compare as always equal.
    if (requiredLevel === -1 || currentLevel === -1) return false;

    return currentLevel >= requiredLevel;
  }

  /**
   * Treats owner and organization-owner as equivalent.
   */
  private rolesMatch(current: string, required: string): boolean {
    const ownerAliases = new Set(['owner', 'organization-owner']);
    if (ownerAliases.has(current) && ownerAliases.has(required)) {
      return true;
    }
    return current === required;
  }

  /**
   * Utility — returns current normalized role for templates or logs
   */
  getCurrentRole(): string | null {
    return this.normalizeRole(this.role());
  }
}
